<template>
  <div
    v-click-outside="close"
    class="relative"
    :data-testid="dataTestid"
    @keydown="handleKeydown"
  >
    <!--
      The closed control is a button (it shows the flag, which an <input> cannot). Opening it
      moves focus into the search box, which is the combobox while the list is open
      (WAI-ARIA combobox with list autocomplete): it owns aria-activedescendant, so screen
      readers announce each option as the arrow keys reach it.
    -->
    <button
      :id="buttonId"
      ref="buttonRef"
      type="button"
      aria-haspopup="listbox"
      :aria-expanded="isOpen ? 'true' : 'false'"
      :aria-controls="listId"
      :aria-label="selected ? `Country code: ${selected.name} ${selected.phoneCode}` : 'Country code'"
      :aria-invalid="hasError ? 'true' : undefined"
      :disabled="disabled"
      :data-testid="dataTestid ? `${dataTestid}-btn` : undefined"
      class="flex h-12 w-full items-center gap-2 rounded-lg border px-3 text-base text-semantic-surface-on-surface outline-none transition-colors focus-visible:ring-4 focus-visible:ring-semantic-surface-container-high"
      :class="buttonClass"
      @click="toggle"
    >
      <img
        v-if="selected"
        :src="selected.flag"
        alt=""
        width="24"
        height="18"
        class="h-[1.125rem] w-6 shrink-0 object-contain"
      />
      <span v-if="selected">{{ selected.phoneCode }}</span>
      <V2IconsCaret
        :direction="isOpen ? 'top' : 'bottom'"
        fill=""
        class="ml-auto h-5 w-5 shrink-0"
        :class="disabled ? 'fill-semantic-states-disabled-on-default' : 'fill-semantic-surface-on-surface-variant'"
      />
    </button>

    <div
      v-show="isOpen"
      ref="popupRef"
      :data-placement="placement"
      :data-testid="dataTestid ? `${dataTestid}-popup` : undefined"
      class="absolute left-0 z-20 w-72 max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border border-semantic-outline-variant bg-semantic-surface-default shadow-elevation-2"
      :class="placement === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'"
    >
      <div class="border-b border-semantic-outline-variant p-2">
        <input
          ref="searchRef"
          v-model="query"
          type="text"
          role="combobox"
          aria-autocomplete="list"
          :aria-expanded="isOpen ? 'true' : 'false'"
          :aria-controls="listId"
          :aria-activedescendant="isOpen && filteredCountries.length ? optionId(activeIndex) : undefined"
          aria-label="Search countries"
          autocomplete="off"
          enterkeyhint="done"
          placeholder="Search country or code"
          :data-testid="dataTestid ? `${dataTestid}-search-input` : undefined"
          class="h-10 w-full rounded-lg border border-semantic-outline-variant bg-semantic-surface-default px-3 text-base text-semantic-surface-on-surface outline-none placeholder:text-semantic-surface-on-surface-variant focus:border-semantic-primary-default"
          @input="activeIndex = 0"
        />
      </div>

      <ul
        :id="listId"
        ref="listRef"
        role="listbox"
        aria-label="Countries"
        class="overflow-y-auto py-1"
        :style="{ maxHeight: `${listMaxHeight}px` }"
      >
        <li
          v-for="(country, index) in filteredCountries"
          :id="optionId(index)"
          :key="country.iso"
          role="option"
          :aria-selected="country.iso === modelValue ? 'true' : 'false'"
          :data-active="index === activeIndex ? 'true' : undefined"
          :data-testid="dataTestid ? `${dataTestid}-option-${country.iso.toLowerCase()}` : undefined"
          class="flex cursor-pointer items-center gap-3 px-3 py-2 text-base text-semantic-surface-on-surface"
          :class="{
            'bg-semantic-surface-container-low outline outline-2 -outline-offset-2 outline-semantic-primary-default': index === activeIndex,
            'font-semibold': country.iso === modelValue
          }"
          @mousedown.prevent
          @mousemove="activeIndex = index"
          @click="select(country)"
        >
          <img
            :src="country.flag"
            alt=""
            width="24"
            height="18"
            loading="lazy"
            class="h-[1.125rem] w-6 shrink-0 object-contain"
          />
          <span class="flex-1">{{ country.name }}</span>
          <span class="shrink-0 text-semantic-surface-on-surface-variant">{{ country.phoneCode }}</span>
          <V2IconsCheck
            v-if="country.iso === modelValue"
            class="h-4 w-4 shrink-0"
            fill="currentColor"
            aria-hidden="true"
          />
        </li>
        <li
          v-if="!filteredCountries.length"
          role="presentation"
          :data-testid="dataTestid ? `${dataTestid}-no-results` : undefined"
          class="px-3 py-2 text-sm text-semantic-surface-on-surface-variant"
        >
          No countries match "{{ query }}"
        </li>
      </ul>
    </div>

    <!-- Search results, announced once typing settles (the list itself is not a live region). -->
    <p
      class="sr-only"
      aria-live="polite"
      :data-testid="dataTestid ? `${dataTestid}-status` : undefined"
    >
      {{ statusMessage }}
    </p>
  </div>
</template>

<script setup>
import { DEFAULT_PHONE_COUNTRY, PHONE_COUNTRIES } from '~/constants/country.constants'

defineOptions({
  name: 'V2AtomsCountryCodeSelect'
})

const props = defineProps({
  /** ISO 3166-1 alpha-2 code of the selected country, e.g. 'AU'. */
  modelValue: {
    type: String,
    default: DEFAULT_PHONE_COUNTRY
  },
  /** Countries to offer. Australia is always listed first, whatever order these are in. */
  countries: {
    type: Array,
    default: () => PHONE_COUNTRIES
  },
  disabled: {
    type: Boolean,
    default: false
  },
  /** Only switches the border to the error state; the parent field renders the message. */
  hasError: {
    type: Boolean,
    default: false
  },
  /** Base for the element ids, so a field can point its label at the button. */
  id: {
    type: String,
    default: ''
  },
  dataTestid: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:modelValue'])

// List height bounds: the default, and the least worth showing before flipping is better.
const LIST_MAX_HEIGHT = 256
const LIST_MIN_HEIGHT = 120
// Gap kept between the popup and the viewport edge.
const VIEWPORT_MARGIN = 8

const uid = useId()
const buttonId = computed(() => props.id || `country-code-${uid}`)
const listId = computed(() => `${buttonId.value}-list`)
const optionId = index => `${buttonId.value}-option-${index}`

const buttonRef = ref(null)
const searchRef = ref(null)
const listRef = ref(null)
const popupRef = ref(null)
const isOpen = ref(false)
const activeIndex = ref(0)
const query = ref('')
const placement = ref('bottom')
const listMaxHeight = ref(LIST_MAX_HEIGHT)

const orderedCountries = computed(() => {
  const list = [...props.countries]
  const australia = list.findIndex(country => country.iso === DEFAULT_PHONE_COUNTRY)
  if (australia > 0) list.unshift(...list.splice(australia, 1))
  return list
})

/*
 * Matches the name anywhere ("zeal" → New Zealand), the ISO code exactly ("in"), or the
 * dialling code by its leading digits ("91", "+91", "+1 2").
 */
const filteredCountries = computed(() => {
  const text = query.value.trim().toLowerCase()
  if (!text) return orderedCountries.value

  const digits = text.replace(/\D/g, '')
  const isCodeSearch = /^\+?[\d\s]+$/.test(text)

  return orderedCountries.value.filter(country => {
    if (isCodeSearch) return country.phoneCode.replace(/\D/g, '').startsWith(digits)
    return country.name.toLowerCase().includes(text) || country.iso.toLowerCase() === text
  })
})

const statusMessage = computed(() => {
  if (!isOpen.value || !query.value.trim()) return ''
  const count = filteredCountries.value.length
  if (!count) return 'No countries found'
  return `${count} ${count === 1 ? 'country' : 'countries'} found`
})

const selected = computed(() => orderedCountries.value.find(country => country.iso === props.modelValue)
  || orderedCountries.value[0])

const buttonClass = computed(() => {
  if (props.disabled) return 'cursor-not-allowed bg-semantic-states-disabled-default border-semantic-outline-variant'
  if (props.hasError) return 'bg-semantic-surface-default border-semantic-states-error-default'
  if (isOpen.value) return 'bg-semantic-surface-default border-semantic-primary-default'
  return 'bg-semantic-surface-default border-semantic-outline-variant hover:border-semantic-outline-default'
})

/*
 * Opens downward when it fits, upward when the space above is larger, and caps the list to
 * whichever side it uses. The visual viewport is what is actually visible: on a phone it
 * shrinks when the keyboard opens for the search box, which the layout viewport ignores.
 */
function updatePlacement () {
  const button = buttonRef.value
  if (!button || typeof window === 'undefined') return

  const rect = button.getBoundingClientRect()
  const viewport = window.visualViewport
  const viewportTop = viewport?.offsetTop || 0
  const viewportBottom = viewportTop + (viewport?.height || window.innerHeight)

  const searchHeight = popupRef.value?.firstElementChild?.offsetHeight || 57
  const wanted = searchHeight + LIST_MAX_HEIGHT
  const below = viewportBottom - rect.bottom - VIEWPORT_MARGIN
  const above = rect.top - viewportTop - VIEWPORT_MARGIN

  placement.value = below >= wanted || below >= above ? 'bottom' : 'top'
  const room = (placement.value === 'bottom' ? below : above) - searchHeight
  listMaxHeight.value = Math.max(LIST_MIN_HEIGHT, Math.min(LIST_MAX_HEIGHT, Math.floor(room)))
}

function addViewportListeners () {
  window.addEventListener('resize', updatePlacement)
  window.addEventListener('scroll', updatePlacement, true)
  window.visualViewport?.addEventListener('resize', updatePlacement)
}

function removeViewportListeners () {
  if (typeof window === 'undefined') return
  window.removeEventListener('resize', updatePlacement)
  window.removeEventListener('scroll', updatePlacement, true)
  window.visualViewport?.removeEventListener('resize', updatePlacement)
}

function open (initialQuery = '') {
  if (props.disabled) return
  query.value = initialQuery
  activeIndex.value = initialQuery
    ? 0
    : Math.max(filteredCountries.value.findIndex(country => country.iso === selected.value?.iso), 0)
  updatePlacement()
  isOpen.value = true
  addViewportListeners()
  nextTick(() => {
    updatePlacement()
    searchRef.value?.focus({ preventScroll: true })
    scrollActiveIntoView()
  })
}

function close () {
  if (!isOpen.value) return
  isOpen.value = false
  query.value = ''
  removeViewportListeners()
}

onBeforeUnmount(removeViewportListeners)

function toggle () {
  if (isOpen.value) close()
  else open()
}

function select (country) {
  if (!country) return
  if (country.iso !== props.modelValue) emit('update:modelValue', country.iso)
  close()
  buttonRef.value?.focus()
}

function move (step) {
  const last = filteredCountries.value.length - 1
  activeIndex.value = Math.min(Math.max(activeIndex.value + step, 0), Math.max(last, 0))
  nextTick(scrollActiveIntoView)
}

function scrollActiveIntoView () {
  listRef.value?.querySelector(`#${CSS.escape(optionId(activeIndex.value))}`)?.scrollIntoView?.({ block: 'nearest' })
}

const isPrintable = event => event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey

function handleKeydown (event) {
  if (props.disabled) return

  if (!isOpen.value) {
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
      event.preventDefault()
      open()
    } else if (isPrintable(event)) {
      // Type-to-search straight from the closed button.
      event.preventDefault()
      open(event.key)
    }
    return
  }

  const inSearch = event.target === searchRef.value

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      move(1)
      break
    case 'ArrowUp':
      event.preventDefault()
      move(-1)
      break
    case 'PageDown':
      event.preventDefault()
      move(10)
      break
    case 'PageUp':
      event.preventDefault()
      move(-10)
      break
    case 'Home':
    case 'End':
      // In the search box these move the caret, as in any text field.
      if (inSearch) break
      event.preventDefault()
      activeIndex.value = event.key === 'Home' ? 0 : Math.max(filteredCountries.value.length - 1, 0)
      nextTick(scrollActiveIntoView)
      break
    case 'Enter':
      event.preventDefault()
      select(filteredCountries.value[activeIndex.value])
      break
    case ' ':
      // A space is part of a search ("New Zealand"); it only selects from the button.
      if (inSearch) break
      event.preventDefault()
      select(filteredCountries.value[activeIndex.value])
      break
    case 'Escape':
      event.preventDefault()
      close()
      buttonRef.value?.focus()
      break
    case 'Tab':
      close()
      break
    default:
      break
  }
}

defineExpose({ open, close, focus: () => buttonRef.value?.focus() })
</script>
